import {
  CSS_BRACKET_PAIRS,
  CSS_CLOSING_BRACKETS,
  CSS_ESCAPE_RE,
  CSS_NEWLINES,
  CSS_QUOTES,
  CSS_REPLACEMENT_CHARACTER,
  HEX_RADIX,
  MAX_CODE_POINT,
  NOTICE_CUSTOM_CSS_MAX_LENGTH,
  NOTICE_DISALLOWED_CSS_TOKENS,
  SURROGATE_MAX,
  SURROGATE_MIN,
} from "./constants";

/** Resolves CSS escapes the way a tokenizer does, so a token check sees through them. */
const decodeCssEscapes = (value: string): string =>
  value.replace(
    CSS_ESCAPE_RE,
    (_match, hex: string | undefined, literal: string) => {
      if (hex === undefined) {
        return literal;
      }
      const codePoint = Number.parseInt(hex, HEX_RADIX);
      if (
        codePoint === 0 ||
        codePoint > MAX_CODE_POINT ||
        (codePoint >= SURROGATE_MIN && codePoint <= SURROGATE_MAX)
      ) {
        return CSS_REPLACEMENT_CHARACTER;
      }
      return String.fromCodePoint(codePoint);
    },
  );

/**
 * Whether every `{`, `(` and `[` is closed by its own partner, in order,
 * reading the CSS the way a tokenizer does: brackets inside comments, strings
 * and escapes don't count. The notice wraps the CSS in an `@scope` block, so a
 * stray `}` would close that block and let the rules after it style the host
 * page. A string a newline cuts short, or a comment or string left open, is
 * refused too, since the browser would read the brackets after it differently.
 */
const hasBalancedBlocks = (css: string): boolean => {
  const open: string[] = [];
  let quote: string | undefined;
  let inComment = false;
  for (let index = 0; index < css.length; index += 1) {
    const char = css[index];
    if (inComment) {
      if (char === "*" && css[index + 1] === "/") {
        inComment = false;
        index += 1;
      }
    } else if (char === "\\") {
      index += 1;
    } else if (quote) {
      if (char === quote) {
        quote = undefined;
      } else if (CSS_NEWLINES.includes(char)) {
        return false;
      }
    } else if (char === "/" && css[index + 1] === "*") {
      inComment = true;
      index += 1;
    } else if (CSS_QUOTES.includes(char)) {
      quote = char;
    } else if (char in CSS_BRACKET_PAIRS) {
      open.push(CSS_BRACKET_PAIRS[char]);
    } else if (CSS_CLOSING_BRACKETS.includes(char) && open.pop() !== char) {
      return false;
    }
  }
  return open.length === 0 && !quote && !inComment;
};

/**
 * The server's custom CSS guard: within the length cap, balanced so it stays
 * inside the notice's `@scope`, and free of every disallowed token both as
 * written and once escapes are decoded.
 */
export const isSafeCustomCss = (css: string): boolean => {
  if (css.length > NOTICE_CUSTOM_CSS_MAX_LENGTH || !hasBalancedBlocks(css)) {
    return false;
  }
  const lowered = css.toLowerCase();
  const decoded = decodeCssEscapes(css).toLowerCase();
  return NOTICE_DISALLOWED_CSS_TOKENS.every(
    (token) => !lowered.includes(token) && !decoded.includes(token),
  );
};
